export const STORAGE_KEY = 'marketplace_install_requests';
export const EXPIRATION_DAYS = 5;
export const EXPIRATION_MS = EXPIRATION_DAYS * 24 * 60 * 60 * 1000;

interface InstallRequestData {
  timestamp: number;
}

interface InstallRequestsStore {
  [listingId: string]: InstallRequestData;
}

/**
 * Safely gets the install requests from localStorage
 */
function getInstallRequests(): InstallRequestsStore {
  try {
    const data = localStorage.getItem(STORAGE_KEY);
    if (!data) {
      return {};
    }
    return JSON.parse(data) as InstallRequestsStore;
  } catch {
    // Handle JSON parse errors or localStorage unavailable (incognito mode)
    return {};
  }
}

/**
 * Safely saves install requests to localStorage
 */
function saveInstallRequests(requests: InstallRequestsStore): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(requests));
  } catch {
    // Handle QuotaExceededError or SecurityError - fail silently
  }
}

/**
 * Records that an install request was sent for a specific listing
 */
export function addInstallRequest(listingId: number): void {
  const requests = getInstallRequests();
  requests[String(listingId)] = {
    timestamp: Date.now(),
  };
  saveInstallRequests(requests);
}

/**
 * Checks if there's an active (non-expired) install request for a listing
 */
export function hasActiveInstallRequest(listingId: number): boolean {
  const requests = getInstallRequests();
  const request = requests[String(listingId)];

  if (!request) {
    return false;
  }

  const isExpired = Date.now() - request.timestamp > EXPIRATION_MS;
  return !isExpired;
}

/**
 * Removes expired install requests from localStorage
 */
export function cleanupExpiredRequests(): void {
  const requests = getInstallRequests();
  const now = Date.now();
  let hasChanges = false;

  const cleanedRequests: InstallRequestsStore = {};

  Object.keys(requests).forEach((listingId) => {
    const request = requests[listingId];
    const isExpired = now - request.timestamp > EXPIRATION_MS;

    if (!isExpired) {
      cleanedRequests[listingId] = request;
    } else {
      hasChanges = true;
    }
  });

  if (hasChanges) {
    saveInstallRequests(cleanedRequests);
  }
}
